import React, { useEffect, useState } from 'react';
import { initPortalI18n, PortalLanguageToggle } from '@shared/i18n';
import { useIsMobile } from '@shared/hooks';
import { TableColumnPrefsFromUser } from '../data-grid/table-column-prefs';
import { UnifiedTopbar } from './UnifiedTopbar';
import { UnifiedSidebar } from './UnifiedSidebar';
import { UnifiedBreadcrumb } from './UnifiedBreadcrumb';
import { UnifiedFooter } from './UnifiedFooter';
import type { UnifiedLayoutProps, UnifiedSidebarProps, BreadcrumbItem } from './Layout.types';

export type { UnifiedLayoutProps } from './Layout.types';

export function UnifiedLayout({
  logoText,
  entryUrl = '/entry',
  onLogoClick,
  user,
  isAuthenticated,
  onLogout,
  onExtendToken,
  serviceName,
  serviceHref,
  sidebar,
  customSidebar,
  categoryOrder,
  tabs,
  activeTab,
  collapsible,
  onTabClick,
  children,
  searchValue,
  onSearchChange,
  searchPlaceholder,
  topbarExtra,
  breadcrumbItems: externalBreadcrumbItems,
  sidebarSubtitleColor,
  sidebarExpandedWidthRem,
  sidebarHeaderIcon,
  userMenuLabels,
  showLanguageToggle = true,
}: UnifiedLayoutProps) {
  const isMobile = useIsMobile();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    void initPortalI18n();
  }, []);

  const sidebarProps: UnifiedSidebarProps | undefined = sidebar ?? (
    tabs ? {
      categoryOrder,
      tabs,
      activeTab,
      collapsible,
      onTabClick,
      subtitle: serviceName,
      subtitleColor: sidebarSubtitleColor,
      expandedWidthRem: sidebarExpandedWidthRem,
      headerIcon: sidebarHeaderIcon,
    } : undefined
  );

  const breadcrumbItems: BreadcrumbItem[] = externalBreadcrumbItems ?? [
    { label: serviceName, href: serviceHref },
    ...(sidebarProps?.tabs.find(t => t.id === sidebarProps.activeTab)?.label 
      ? [{ label: sidebarProps.tabs.find(t => t.id === sidebarProps.activeTab)!.label }] 
      : []),
  ];

  const handleMobileTabClick = (tab: any) => {
    setMobileMenuOpen(false);
    onTabClick?.(tab);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
      <UnifiedTopbar
        logoText={logoText}
        entryUrl={entryUrl}
        onLogoClick={onLogoClick}
        user={user}
        isAuthenticated={isAuthenticated}
        onLogout={onLogout}
        onExtendToken={onExtendToken}
        searchValue={searchValue}
        onSearchChange={onSearchChange}
        searchPlaceholder={searchPlaceholder}
        topbarExtra={
          <>
            {showLanguageToggle ? <PortalLanguageToggle /> : null}
            {topbarExtra}
          </>
        }
        userMenuLabels={userMenuLabels}
        isMobile={isMobile}
        onMenuClick={() => setMobileMenuOpen(!mobileMenuOpen)}
      />
      <div style={{ display: 'flex', flex: 1, minHeight: 0, position: 'relative' }}>
        {/* Mobile Overlay */}
        {isMobile && mobileMenuOpen && (
          <div
            onClick={() => setMobileMenuOpen(false)}
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(0,0,0,0.3)',
              zIndex: 39,
            }}
          />
        )}
        
        {customSidebar && React.cloneElement(customSidebar as React.ReactElement, {
          mobileOpen: mobileMenuOpen,
          onMobileClose: () => setMobileMenuOpen(false),
          serviceName,
          subtitleColor: sidebarSubtitleColor,
        } as any)}
        {!customSidebar && sidebarProps && (
          <UnifiedSidebar
            {...sidebarProps}
            isMobile={isMobile}
            mobileOpen={mobileMenuOpen}
            onTabClick={isMobile ? handleMobileTabClick : sidebarProps.onTabClick}
          />
        )}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0 }}>
          <UnifiedBreadcrumb items={breadcrumbItems} homeUrl={entryUrl} />
          <main style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', minWidth: 0, backgroundColor: 'hsl(0 0% 98%)' }}>
            <TableColumnPrefsFromUser user={user}>{children}</TableColumnPrefsFromUser>
          </main>
        </div>
      </div>
      <UnifiedFooter />
    </div>
  );
}
